'use client';

import { Skeleton } from '@common/components';
import { SvgCheck } from '@common/icon';
import { cn } from '@core/utils';

interface FilterSelectableListProps<T extends string | number> {
  options: { value: T; label: string }[];
  selectedValue: T | null;
  onSelect: (value: T | null) => void;
  allowClear?: boolean;
  allLabel?: string;
  isLoading?: boolean;
  skeletonCount?: number;
}

export function FilterSelectableList<T extends string | number>({
  options,
  selectedValue,
  onSelect,
  allowClear = false,
  allLabel = '전체',
  isLoading = false,
  skeletonCount = 6,
}: FilterSelectableListProps<T>) {
  if (isLoading) {
    /* 실제 button: `flex items-center justify-between py-4 typo-body4`
     * typo-body4 line-height 1.5 × 14 = 21 + py-4(8) = 29px row */
    return (
      <>
        {Array.from({ length: skeletonCount }, (_, index) => (
          <div key={index} className="flex items-center py-4">
            <Skeleton className="h-21 w-3/5 rounded-xxsmall" />
          </div>
        ))}
      </>
    );
  }

  const renderRow = (
    key: string,
    label: string,
    isSelected: boolean,
    onClick: () => void,
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between py-4 text-left tracking-[-0.28px] text-Gray-800 typo-body4',
        isSelected && 'font-600 text-B-500',
      )}
    >
      <span>{label}</span>
      {isSelected ? (
        <SvgCheck className="size-16 stroke-B-500 text-B-500" strokeWidth={1} />
      ) : null}
    </button>
  );

  return (
    <>
      {allowClear
        ? renderRow('__all__', allLabel, selectedValue === null, () =>
            onSelect(null),
          )
        : null}
      {options.map((option) =>
        renderRow(
          String(option.value),
          option.label,
          selectedValue === option.value,
          () => onSelect(option.value),
        ),
      )}
    </>
  );
}
